/* ===================================================================
   Language registry
   ===================================================================
   One place that answers "what languages exist, and what does each
   one imply?" — direction, Intl locale, font stack, native label.
   Everything else (the pre-paint script in index.html, the switcher,
   the formatters, the CSS) reads from here rather than re-deciding.
   =================================================================== */

// Same key the pre-paint script in public/index.html reads. Kept in the
// 'jobcard-*' namespace alongside 'jobcard-theme'.
export const STORAGE_KEY = 'jobcard-lang';

export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGES = {
  en: {
    code: 'en',
    dir: 'ltr',
    // English label, for the switcher's secondary line and aria-labels.
    label: 'English',
    // What a speaker of the language calls it — the primary switcher line.
    nativeName: 'English',
    // Intl locale. '-u-nu-latn' pins the numbering system to 0-9 so a
    // price, plate, or invoice reference reads the same in every language
    // (see utils/format.js for why that matters here).
    locale: 'en-AE-u-nu-latn',
  },
  ar: {
    code: 'ar',
    dir: 'rtl',
    label: 'Arabic',
    nativeName: 'العربية',
    locale: 'ar-AE-u-nu-latn',
  },
  ur: {
    code: 'ur',
    dir: 'rtl',
    label: 'Urdu',
    nativeName: 'اردو',
    locale: 'ur-PK-u-nu-latn',
  },
};

export const SUPPORTED_LANGUAGES = Object.keys(LANGUAGES);

/** Languages written right-to-left. Drives <html dir> and the `rtl:` variants. */
export const RTL_LANGUAGES = SUPPORTED_LANGUAGES.filter((c) => LANGUAGES[c].dir === 'rtl');

/**
 * Normalises anything language-shaped ('ar-AE', 'AR', undefined) to a code we
 * actually ship. i18next hands us regional tags; the CSS and the switcher want
 * the bare code.
 */
export function normalizeLanguage(value) {
  if (!value) return DEFAULT_LANGUAGE;
  const base = String(value).toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : DEFAULT_LANGUAGE;
}

export function getDirection(language) {
  return LANGUAGES[normalizeLanguage(language)].dir;
}

export function isRTL(language) {
  return getDirection(language) === 'rtl';
}

/** Full Intl locale for a language code, e.g. 'ar' -> 'ar-AE-u-nu-latn'. */
export function getLocale(language) {
  return LANGUAGES[normalizeLanguage(language)].locale;
}

/* ===================================================================
   Language in the URL
   ===================================================================
   The public, indexable pages ('/', '/terms', '/PrivacyPolicy') are
   served at a per-language path: '/ar/terms', '/ur', and so on.

   This exists for one reason: a search engine indexes URLs, not
   localStorage. With a single URL serving all three languages, only
   whichever one the crawler happened to render can ever be indexed, and
   hreflang has nothing to point at. Distinct paths give each language a
   real address, and cost nothing server-side because the Express app
   already falls back to index.html for unknown paths.

   English is deliberately UNPREFIXED. It is the x-default and the
   existing '/' has whatever ranking history the site has; moving it to
   '/en' would throw that away for no benefit.

   The authenticated app is not prefixed either — it is noindex and
   behind a login, so a crawlable address buys nothing and every route
   would need duplicating.
   =================================================================== */

/** Languages that appear as a URL prefix — i.e. everything except the default. */
export const PREFIXED_LANGUAGES = SUPPORTED_LANGUAGES.filter((c) => c !== DEFAULT_LANGUAGE);

/** Public paths that exist in every language. Keep in sync with the sitemap. */
export const PUBLIC_PATHS = ['/', '/terms', '/PrivacyPolicy', '/support'];

/**
 * Reads the language a path is asking for, or null if it carries no prefix.
 *
 * Returns null rather than the default language so callers can tell "this URL
 * says English" apart from "this URL says nothing" — only the latter should
 * defer to a stored preference.
 */
export function getLanguageFromPath(pathname = '') {
  const match = String(pathname).match(/^\/([a-z]{2})(?:\/|$)/i);
  if (!match) return null;
  const code = match[1].toLowerCase();
  return PREFIXED_LANGUAGES.includes(code) ? code : null;
}

/** '/ar/terms' -> '/terms'. Paths without a prefix come back untouched. */
export function stripLanguagePrefix(pathname = '/') {
  const lang = getLanguageFromPath(pathname);
  if (!lang) return pathname || '/';
  const rest = pathname.slice(lang.length + 1);
  return rest === '' ? '/' : rest;
}

/** '/terms' + 'ar' -> '/ar/terms'. English returns the bare path. */
export function addLanguagePrefix(pathname = '/', language = DEFAULT_LANGUAGE) {
  const lang = normalizeLanguage(language);
  const bare = stripLanguagePrefix(pathname);
  if (lang === DEFAULT_LANGUAGE) return bare;
  return bare === '/' ? `/${lang}` : `/${lang}${bare}`;
}

/** Absolute URLs for every language of a path — the hreflang set. */
export function getAlternateUrls(pathname = '/', origin = 'https://jobcard.ae') {
  return SUPPORTED_LANGUAGES.map((code) => ({
    language: code,
    url: `${origin}${addLanguagePrefix(pathname, code)}`,
  }));
}

/** True when this path is one of the public pages that exists per language. */
export function isPublicPath(pathname = '/') {
  const bare = stripLanguagePrefix(pathname);
  return PUBLIC_PATHS.some((p) => p.toLowerCase() === bare.toLowerCase());
}
